import type { CapacityAllocationMap } from '@/types/capacityCalendar';

const DAYS_PER_WEEK = 7;

// weekKey is a "YYYY-MM-DD" Monday date (see calendarBuckets.ts's toDateKey) — shifting via
// Date.UTC avoids local-timezone/DST drift and normalizes cleanly across month/year
// boundaries (e.g. 2026-08-03 minus 7 days -> 2026-07-27).
function shiftWeekKey(weekKey: string, days: number): string {
  const [year, month, day] = weekKey.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  const shiftedYear = shifted.getUTCFullYear();
  const shiftedMonth = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const shiftedDay = String(shifted.getUTCDate()).padStart(2, '0');
  return `${shiftedYear}-${shiftedMonth}-${shiftedDay}`;
}

export interface PreviousWeekPlan {
  capacity: number;
  capacityFactor: number | null;
}

// Resolves what the no-plan cell's "Copy previous week" line should show/create: the prior
// week's raw (vendor-reported) capacity and factor, read straight off the calendar store's
// current allocations — never the pending-import preview, which hasn't been applied yet.
// Returns null when the previous week has no plan (or no usable capacity value), so the
// micro-menu's copy line simply doesn't render.
export function resolvePreviousWeekPlan(rowId: string, weekKey: string, allocations: CapacityAllocationMap): PreviousWeekPlan | null {
  const previousWeekKey = shiftWeekKey(weekKey, -DAYS_PER_WEEK);
  const previous = allocations[rowId]?.[previousWeekKey];
  if (!previous) {
    return null;
  }

  const capacity = previous.capacity ?? previous.planningCapacity;
  if (capacity === null || capacity === undefined) {
    return null;
  }

  return {
    capacity,
    capacityFactor: previous.capacityFactor ?? null,
  };
}
